import Fuse from "fuse.js";
import { normalizeContactName } from "./misc";

// Duplicate-company detection, shared by the forms that create a company or
// link a person to one. A company can already exist in three shapes:
//
//   - as a company ACCOUNT (`companies/{id}`);
//   - as a company CONTACT, the "General Contact" a salesperson entered
//     before anyone made an account for it;
//   - only as the free-text `company` on the PEOPLE who work there.
//
// The server's copy of companyKey and memberContactType is
// functions/companyMatch.js; tests/unit/util/contactMatch.parity.spec.js keeps
// them agreeing.
//
// A match is STRONG when the names agree once spelling that separates nothing
// is set aside (see companyKey), or when an account or company contact shares
// a phone number, street address or company email domain with the company
// being made: near-certain to be the same company or a related one, so a form
// may refuse to save until someone decides. Each match says what it shares
// (`on`). A WEAK match only looks alike by name (fuzzy) and is just shown.

export const MATCH_MIN_LENGTH = 5;

// Same as the duplicate finder's name threshold (ContactDuplicates).
const WEAK_THRESHOLD = 0.1;

// Words that end a business name without telling two businesses apart, so
// "Speich & Co." and "Speich and Company" are one company.
const TRAILING_WORDS = new Set([
  "and",
  "co",
  "company",
  "corp",
  "corporation",
  "inc",
  "incorporated",
  "llc",
  "ltd",
  "limited",
]);

// The comparison key for a business name: normalized (normalizeContactName),
// with a leading "the" and any trailing words above removed, and no spaces
// ("Safe Zone" is "SafeZone"). Never removes the last word, so a name made
// only of them still has a key.
export const companyKey = (name) => {
  const words = normalizeContactName(name).split(" ").filter(Boolean);
  if (words.length > 1 && words[0] === "the") {
    words.shift();
  }
  while (words.length > 1 && TRAILING_WORDS.has(words[words.length - 1])) {
    words.pop();
  }
  return words.join("");
};

// The contact type a company's members take: the company's own type for a
// customer company; "vendor" for a vendor, or "contractor" (a builder) for a
// labor vendor.
export const memberContactType = ({ type, vendorType } = {}) =>
  type === "vendor" ? (vendorType === "labor" ? "contractor" : "vendor") : type;

// A company's own contact, which mirrors its account (or stands in for one).
export const isGeneralContact = (contact) =>
  Boolean(contact) && contact.position === "General Contact";

// Email domains anyone can have an address at: sharing one says nothing.
// Free mail, and internet providers' mail (fuse.net is Cincinnati Bell's).
const SHARED_DOMAINS = new Set([
  "aol.com",
  "att.net",
  "bellsouth.net",
  "charter.net",
  "columbus.rr.com",
  "comcast.net",
  "earthlink.net",
  "frontier.com",
  "fuse.net",
  "gmail.com",
  "hotmail.com",
  "icloud.com",
  "live.com",
  "mac.com",
  "mail.com",
  "me.com",
  "msn.com",
  "outlook.com",
  "protonmail.com",
  "rr.com",
  "sbcglobal.net",
  "verizon.net",
  "windstream.net",
  "wowway.com",
  "yahoo.com",
  "ymail.com",
]);

// A phone's digits, without a leading US 1. Short numbers (an extension, a
// half-typed number) match nothing.
export const phoneKey = (phone) => {
  const digits = String((phone && phone.number) || "")
    .replace(/\D/g, "")
    .replace(/^1(?=\d{10}$)/, "");
  return digits.length >= 10 ? digits : "";
};

// An email's domain, unless anyone can have an address there.
export const emailDomain = (email) => {
  const domain = String((email && email.address) || "")
    .trim()
    .toLowerCase()
    .split("@")[1];
  return domain && !SHARED_DOMAINS.has(domain) ? domain : "";
};

// Street and five-digit ZIP, with spelling that separates nothing (case,
// punctuation, "St"/"Street", a suite) set aside.
const STREET_WORDS =
  /\b(street|st|road|rd|avenue|ave|drive|dr|lane|ln|court|ct|boulevard|blvd|suite|ste|unit|apt)\b/g;
export const addressKey = (address) => {
  const street = String((address && address.street) || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(STREET_WORDS, " ")
    .replace(/\s+/g, " ")
    .trim();
  const zip = String((address && address.zip) || "").slice(0, 5);
  return street && zip ? `${street}|${zip}` : "";
};

// The keys a company's details match on: `{ phone, address, domain }`, each a
// list. `memberEmails` are its people's addresses: their domain is the
// company's too.
export const detailKeys = ({
  phones = [],
  addresses = [],
  emails = [],
  memberEmails = [],
} = {}) => ({
  phone: phones.map(phoneKey).filter(Boolean),
  address: addresses.map(addressKey).filter(Boolean),
  domain: [...emails, ...memberEmails].map(emailDomain).filter(Boolean),
});

const DETAILS = ["phone", "address", "domain"];

class MatchGroup {
  // `detailsOf(item)` gives an item's details (see detailKeys); without it
  // the group matches on name only.
  constructor(items, nameOf, detailsOf = null) {
    this.byKey = new Map();
    this.byDetail = { phone: new Map(), address: new Map(), domain: new Map() };
    const add = (map, key, item) => {
      const list = map.get(key) || [];
      if (!list.includes(item)) {
        map.set(key, [...list, item]);
      }
    };
    items.forEach((item) => {
      const key = companyKey(nameOf(item));
      if (key) {
        add(this.byKey, key, item);
      }
      if (detailsOf) {
        const keys = detailKeys(detailsOf(item));
        DETAILS.forEach((detail) =>
          keys[detail].forEach((k) => add(this.byDetail[detail], k, item))
        );
      }
    });
    this.fuse = new Fuse(items, {
      keys: [{ name: "name", getFn: (i) => normalizeContactName(nameOf(i)) }],
      threshold: WEAK_THRESHOLD,
      minMatchCharLength: MATCH_MIN_LENGTH,
    });
  }

  find(name, exclude, details) {
    const found = new Map();
    const strongly = (item, reason) => {
      const match = found.get(item) || { item, strong: true, on: [] };
      if (!match.on.includes(reason)) {
        match.on.push(reason);
      }
      found.set(item, match);
    };
    (this.byKey.get(companyKey(name)) || []).forEach((item) =>
      strongly(item, "name")
    );
    const keys = detailKeys(details);
    DETAILS.forEach((detail) =>
      keys[detail].forEach((k) =>
        (this.byDetail[detail].get(k) || []).forEach((item) =>
          strongly(item, detail)
        )
      )
    );
    const query = normalizeContactName(name);
    if (query.length >= MATCH_MIN_LENGTH) {
      this.fuse.search(query).forEach(({ item }) => {
        if (!found.has(item)) {
          found.set(item, { item, strong: false, on: [] });
        }
      });
    }
    return [...found.values()]
      .filter(({ item }) => !exclude.includes(item.id))
      .sort((a, b) => b.strong - a.strong);
  }
}

// Builds its indexes once, so `find` is cheap enough to run as someone types.
// A contact already linked to an account is represented by that account and
// is left out; its email still counts towards the account's domains.
export class CompanyMatcher {
  constructor({ companies = [], contacts = [] } = {}) {
    const unlinked = contacts.filter((c) => !c.data.companyID);
    const memberEmails = new Map();
    contacts
      .filter((c) => c.data.companyID)
      .forEach((c) =>
        memberEmails.set(c.data.companyID, [
          ...(memberEmails.get(c.data.companyID) || []),
          ...(c.data.emails || []),
        ])
      );
    const detailsOf = (c) => ({
      ...c.data,
      memberEmails: memberEmails.get(c.id) || [],
    });
    this.accounts = new MatchGroup(companies, (c) => c.data.name, detailsOf);
    this.companyContacts = new MatchGroup(
      unlinked.filter(isGeneralContact),
      (c) => c.data.name,
      detailsOf
    );
    this.people = new MatchGroup(
      unlinked.filter((c) => !isGeneralContact(c) && c.data.company),
      (c) => c.data.company
    );
  }

  // `{ accounts, companyContacts, people }`: lists of `{ item, strong, on }`,
  // strong matches first. `exclude` lists IDs never to return (the company
  // being edited, the contact it is being made from). `details` are the
  // company's phones, addresses, emails and memberEmails (see detailKeys);
  // people are matched on name only.
  find(name, { exclude = [], details = {} } = {}) {
    return {
      accounts: this.accounts.find(name, exclude, details),
      companyContacts: this.companyContacts.find(name, exclude, details),
      people: companyKey(name) ? this.people.find(name, exclude) : [],
    };
  }

  // The account a free-text company name refers to, when exactly one
  // strongly matches it by name.
  findAccount(name) {
    const strong = this.accounts
      .find(name, [])
      .filter((match) => match.on.includes("name"));
    return strong.length === 1 ? strong[0].item : null;
  }
}
